import { useMemo, useState } from "react";
import { Sankey, Tooltip, ResponsiveContainer, Layer, Rectangle } from "recharts";
import { AUD, api } from "../lib/api";
import { Sparkle, Sliders, Play, Pause, Database, ArrowClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isDemoMode } from "../lib/demoStore";
import CashPlan from "./CashPlan";

/**
 * Cash flow "money movie" — Sankey diagram showing where the money actually
 * moves across a full financial year. Editable inputs pre-populated with
 * sensible defaults; user tweaks live and the ribbons re-scale instantly.
 *
 * Modes: Working (salary + super sacrifice) vs Retired (pension + drawdown).
 * Phase 2: Play/Pause animation — dashed strokes flow across every link so
 * you can literally watch the money move.
 *
 * Demo mode uses generic entity names ("Family Business", "Investment Co",
 * "Personal Loan") so the /demo experience is domain-agnostic.
 */

// Labels are demo-aware. Node identifiers use these strings so they must be
// resolved consistently across NODE_COLORS, buildFlows and inputs.
const REAL_LABELS = {
    business_cash: "Pendaz Cash",
    business_income: "Pendaz Business Income",
    business_retained: "Pendaz Retained",
    ext_dividends: "GBE Dividends",
    ext_loan_rep: "GBE Loan Repayment",
    ext_dividends_short: "GBE Dividends (to Pendaz)",
    ext_loan_rep_short: "GBE Loan Repayment (to Pendaz)",
    joe_loan: "Joe Loan",
    joe_loan_input: "Joe Loan Repayment",
    smsf_cc: "SMSF Concessional (Darren+Penny)",
    business_expenses_input: "Pendaz Expenses",
    footer_note: "Retirement mode swaps salary for SMSF pension + portfolio drawdown, but GBE income continues flowing into Pendaz and paying out as director's fees.",
};
const DEMO_LABELS = {
    business_cash: "Family Business Cash",
    business_income: "Family Business Income",
    business_retained: "Business Retained",
    ext_dividends: "Investment Dividends",
    ext_loan_rep: "Loan Interest Received",
    ext_dividends_short: "Investment Dividends",
    ext_loan_rep_short: "Loan Interest Received",
    joe_loan: "Personal Loan",
    joe_loan_input: "Personal Loan Repayment",
    smsf_cc: "SMSF Concessional Contribution",
    business_expenses_input: "Business Expenses",
    footer_note: "Retirement mode swaps salary for SMSF pension + portfolio drawdown, but external business income continues flowing through the family company and paying out as director's fees.",
};
const getLabels = () => (isDemoMode() ? DEMO_LABELS : REAL_LABELS);

// NODE_COLORS keyed on concept-role so both label sets resolve. The
// function accepts a name and returns the appropriate color regardless
// of whether it's the demo or real label.
const COLOR_BY_ROLE = {
    "Salary": "#5F8071",
    "Dividends (Personal)": "#5F8071",
    "Rental Income": "#5F8071",
    "Director's Fees": "#8C6E52",
    "SMSF Pension": "#4A6B7A",
    "Portfolio Drawdown": "#4A6B7A",
    "Personal Cash": "#2E3D3B",
    "SMSF Cash": "#4A6B7A",
    "SMSF Holdings": "#4A6B7A",
    "Lifestyle Expenses": "#C97B5A",
    "Loan Repayments": "#C97B5A",
    "Income Tax": "#C97B5A",
    "Business Expenses": "#C97B5A",
    "Personal Investments": "#5F8071",
    "SMSF Contributions": "#4A6B7A",
    // Business role (either "Pendaz Cash" or "Family Business Cash")
    [REAL_LABELS.business_cash]: "#8C6E52",
    [DEMO_LABELS.business_cash]: "#8C6E52",
    [REAL_LABELS.business_income]: "#8C6E52",
    [DEMO_LABELS.business_income]: "#8C6E52",
    [REAL_LABELS.business_retained]: "#8C6E52",
    [DEMO_LABELS.business_retained]: "#8C6E52",
    [REAL_LABELS.ext_dividends]: "#8C6E52",
    [DEMO_LABELS.ext_dividends]: "#8C6E52",
    [REAL_LABELS.ext_loan_rep]: "#8C6E52",
    [DEMO_LABELS.ext_loan_rep]: "#8C6E52",
    [REAL_LABELS.joe_loan]: "#C97B5A",
    [DEMO_LABELS.joe_loan]: "#C97B5A",
};

// Cadence: sensible AU family defaults ($, per year).
// Salary is modeled as gross → net + PAYG tax + concessional sacrifice
// so the tax outflow and super sacrifice are visible on the ribbon.
const WORKING_DEFAULTS = {
    salary_gross: 250000, salary_tax: 84000,
    dividends_personal: 8000, rental_income: 32000,
    gbe_dividends: 72000, gbe_loan_repayment: 72000,
    directors_fees: 45000, business_income: 260000, business_expenses: 180000,
    lifestyle: 175000, loan_repayments: 62000, joe_loan: 12000,
    personal_invest: 24000,
    // SMSF concessional: $30K Darren + $30K Penny = $60K, salary-sacrificed.
    // NCC currently $0 (bring-forward maxed through FY28 — bump to $240K
    // combined per FY from FY29 when you can do $120K each again).
    smsf_concessional: 60000, smsf_ncc: 0,
    smsf_dividends: 68000, smsf_reinvest: 40000,
};
const RETIRED_DEFAULTS = {
    salary_gross: 0, salary_tax: 0,
    dividends_personal: 12000, rental_income: 32000,
    gbe_dividends: 72000, gbe_loan_repayment: 72000,
    directors_fees: 45000, business_income: 0, business_expenses: 0,
    lifestyle: 175000, loan_repayments: 0, joe_loan: 12000, personal_invest: 0,
    smsf_concessional: 0, smsf_ncc: 0,
    smsf_pension: 78000, portfolio_drawdown: 24000,
    smsf_dividends: 68000, smsf_reinvest: 68000,
};

// Pendaz Retained is the residual — money left inside Pendaz for the year
// after paying business costs and director's fees. Calculated, not entered,
// so it always rebalances when you change any Pendaz-side input.
const pendazRetained = (i) =>
    Math.max(0, (i.business_income || 0) + (i.gbe_dividends || 0) + (i.gbe_loan_repayment || 0)
             - (i.business_expenses || 0) - (i.directors_fees || 0));

function buildFlows(mode, i, L) {
    const nodes = []; const links = [];
    const idx = (n) => {
        let k = nodes.findIndex(x => x.name === n);
        if (k < 0) { nodes.push({ name: n }); k = nodes.length - 1; }
        return k;
    };
    const add = (from, to, v) => { if (v > 0) links.push({ source: idx(from), target: idx(to), value: v }); };

    if (mode === "working") {
        // Salary splits three ways: net take-home, PAYG tax, salary-sacrifice to super.
        const salaryToSMSF = i.smsf_concessional;
        const salaryNet = Math.max(0, (i.salary_gross || 0) - (i.salary_tax || 0) - salaryToSMSF);
        add("Salary",              "Personal Cash",       salaryNet);
        add("Salary",              "Income Tax",          i.salary_tax);
        add("Salary",              "SMSF Contributions",  salaryToSMSF);

        // Other personal inflows
        add("Dividends (Personal)","Personal Cash", i.dividends_personal);
        add("Rental Income",       "Personal Cash", i.rental_income);

        // Business inflows
        add(L.business_income,     L.business_cash, i.business_income);
        add(L.ext_dividends,       L.business_cash, i.gbe_dividends);
        add(L.ext_loan_rep,        L.business_cash, i.gbe_loan_repayment);
        // Business outflows
        add(L.business_cash,       "Business Expenses",  i.business_expenses);
        add(L.business_cash,       "Director's Fees",    i.directors_fees);
        add("Director's Fees",     "Personal Cash",      i.directors_fees);
        add(L.business_cash,       L.business_retained,  pendazRetained(i));
        add("Personal Cash",       "Lifestyle Expenses", i.lifestyle);
        add("Personal Cash",       "Loan Repayments",    i.loan_repayments);
        add("Personal Cash",       L.joe_loan,           i.joe_loan);
        add("Personal Cash",       "Personal Investments", i.personal_invest);
        add("Personal Cash",       "SMSF Contributions", i.smsf_ncc);

        // SMSF internal — net dividends vs reinvestment to avoid a directed
        // cycle (Sankey layout can't handle cycles → infinite recursion).
        add("SMSF Contributions",  "SMSF Cash",  i.smsf_concessional + i.smsf_ncc);
        const netToHoldings = i.smsf_reinvest - i.smsf_dividends;
        if (netToHoldings > 0) add("SMSF Cash", "SMSF Holdings", netToHoldings);
        else add("SMSF Holdings", "SMSF Cash", -netToHoldings);
    } else {
        // Personal inflows (retired)
        add("Dividends (Personal)","Personal Cash", i.dividends_personal);
        add("Rental Income",       "Personal Cash", i.rental_income);
        add("SMSF Pension",        "Personal Cash", i.smsf_pension);
        add("Portfolio Drawdown",  "Personal Cash", i.portfolio_drawdown);
        add("SMSF Cash",           "SMSF Pension",  i.smsf_pension);
        // Business still active — external income continues, paid out as
        // director's fees to the family plus retained inside the company.
        add(L.ext_dividends,       L.business_cash, i.gbe_dividends);
        add(L.ext_loan_rep,        L.business_cash, i.gbe_loan_repayment);
        add(L.business_cash,       "Director's Fees", i.directors_fees);
        add("Director's Fees",     "Personal Cash",   i.directors_fees);
        add(L.business_cash,       L.business_retained, pendazRetained(i));
        // SMSF internal (netted to avoid cycle)
        const netToHoldings = i.smsf_reinvest - i.smsf_dividends;
        if (netToHoldings > 0) add("SMSF Cash", "SMSF Holdings", netToHoldings);
        else add("SMSF Holdings", "SMSF Cash", -netToHoldings);
        // Personal outflows
        add("Personal Cash",       "Lifestyle Expenses", i.lifestyle);
        add("Personal Cash",       "Loan Repayments",    i.loan_repayments);
        add("Personal Cash",       L.joe_loan,           i.joe_loan);
    }
    return { nodes, links };
}

const CustomNode = ({ x, y, width, height, index, payload, containerWidth }) => {
    const name = payload.name;
    const isOut = x + width + 6 > containerWidth - 100;
    return (
        <Layer key={`node-${index}`}>
            <Rectangle x={x} y={y} width={width} height={height}
                       fill={COLOR_BY_ROLE[name] || "#5F8071"} fillOpacity={0.9} />
            <text x={isOut ? x - 8 : x + width + 8} y={y + height / 2}
                  textAnchor={isOut ? "end" : "start"} dominantBaseline="central"
                  fontSize={12} fill="#2E3D3B" style={{ fontFamily: "inherit" }}>
                {name}
            </text>
            <text x={isOut ? x - 8 : x + width + 8} y={y + height / 2 + 14}
                  textAnchor={isOut ? "end" : "start"} dominantBaseline="central"
                  fontSize={10} fill="#6b7770" style={{ fontFamily: "inherit" }}>
                {AUD(payload.value || 0)}
            </text>
        </Layer>
    );
};

// Stable renderer for the Sankey `node` prop — defined at module scope so
// React doesn't remount the whole subtree on every parent re-render.
const renderNode = (props) => <CustomNode {...props} containerWidth={props.containerWidth || 1200} />;

export default function MoneyFlow() {
    const [view, setView] = useState("cash-plan"); // cash-plan | sankey
    const [mode, setMode] = useState("working");
    // Separate input state per mode so toggling doesn't wipe out user edits
    // in the other mode. Merging with `...` would let retirement zeros
    // overwrite working defaults for shared keys (salary, business_income…).
    const [workingInputs, setWorkingInputs] = useState({ ...WORKING_DEFAULTS });
    const [retiredInputs, setRetiredInputs] = useState({ ...RETIRED_DEFAULTS });
    const inputs = mode === "working" ? workingInputs : retiredInputs;
    const setInputs = mode === "working" ? setWorkingInputs : setRetiredInputs;
    const setMany = (patch) => setInputs(s => ({ ...s, ...patch }));

    const [showEdit, setShowEdit] = useState(false);
    const [playing, setPlaying] = useState(false);
    // Live rollup from the Transactions collection. When populated, the
    // matching input keys are overlaid onto the current mode's inputs and a
    // "Source: last 12 mo transactions" chip is shown so the user knows the
    // numbers came from real data (they can still override manually).
    const [rollup, setRollup] = useState(null);
    const [loadingRollup, setLoadingRollup] = useState(false);

    const loadRollup = async () => {
        setLoadingRollup(true);
        try {
            const { data } = await api.get("/money-flow/rollup");
            setRollup(data);
            setMany(data.rollup);
            toast.success(`Loaded ${data.range.months} months of transactions`);
        } catch (e) {
            toast.error("Couldn't load transactions rollup");
        } finally {
            setLoadingRollup(false);
        }
    };

    const clearRollup = () => {
        setRollup(null);
        setInputs(mode === "working" ? { ...WORKING_DEFAULTS } : { ...RETIRED_DEFAULTS });
        toast.success("Reverted to defaults");
    };

    const L = getLabels();
    const { nodes, links } = useMemo(() => buildFlows(mode, inputs, L), [mode, inputs, L]);

    // KPIs: external cash in/out of the family. Investments and internal
    // transfers (SMSF contribs, personal investments, retained earnings) are
    // savings — they show as the "net" bucket, not as outflows.
    const totalIn = mode === "working"
        ? inputs.salary_gross + inputs.dividends_personal + inputs.rental_income +
          inputs.business_income + inputs.gbe_dividends + inputs.gbe_loan_repayment
        : inputs.dividends_personal + inputs.rental_income +
          inputs.smsf_pension + inputs.portfolio_drawdown +
          inputs.gbe_dividends + inputs.gbe_loan_repayment;
    const totalOut = inputs.salary_tax + inputs.lifestyle + inputs.loan_repayments + inputs.joe_loan +
        (mode === "working" ? inputs.business_expenses : 0);
    const netCash = totalIn - totalOut;

    const applyPreset = () => setInputs(mode === "working" ? { ...WORKING_DEFAULTS } : { ...RETIRED_DEFAULTS });

    return (
        <div data-testid="money-flow-page">
            {/* Header + tabs */}
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2 flex items-center gap-2">
                        <Sparkle size={12} weight="fill" className="text-[var(--eucalyptus)]" />
                        Cash flow planner
                    </div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Money movie.</h1>
                    <p className="text-[var(--muted)] mt-3 max-w-2xl text-sm">
                        {view === "cash-plan"
                            ? "How much cash do we need each year — and where's it coming from?"
                            : "The full picture: every ribbon of money that moves across a year. Press Play to watch it flow."}
                    </p>
                </div>
                <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-sm"
                     data-testid="money-view-toggle">
                    <button onClick={() => setView("cash-plan")} data-testid="view-cash-plan"
                            className={`px-4 py-2 transition-colors ${
                                view === "cash-plan" ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                                     : "hover:bg-black/5"}`}>
                        Cash Plan
                    </button>
                    <button onClick={() => setView("sankey")} data-testid="view-sankey"
                            className={`px-4 py-2 transition-colors ${
                                view === "sankey" ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                                  : "hover:bg-black/5"}`}>
                        Sankey (advanced)
                    </button>
                </div>
            </div>

            {view === "cash-plan" && <CashPlan />}

            {view === "sankey" && <MoneyMovieSankey
                mode={mode} setMode={setMode}
                workingInputs={workingInputs} setWorkingInputs={setWorkingInputs}
                retiredInputs={retiredInputs} setRetiredInputs={setRetiredInputs}
                inputs={inputs} setInputs={setInputs} setMany={setMany}
                showEdit={showEdit} setShowEdit={setShowEdit}
                playing={playing} setPlaying={setPlaying}
                rollup={rollup} loadingRollup={loadingRollup}
                loadRollup={loadRollup} clearRollup={clearRollup}
                L={L} nodes={nodes} links={links}
                totalIn={totalIn} totalOut={totalOut} netCash={netCash}
                applyPreset={applyPreset}
            />}
        </div>
    );
}

function MoneyMovieSankey({
    mode, setMode, inputs, setMany,
    showEdit, setShowEdit, playing, setPlaying,
    rollup, loadingRollup, loadRollup, clearRollup,
    L, nodes, links, totalIn, totalOut, netCash, applyPreset,
}) {
    return (
        <div>
            {/* Phase 2 animation: when playing, dash the link strokes and
                animate stroke-dashoffset so beads visibly flow along every
                ribbon. Pure CSS — no framer-motion overhead. */}
            {playing && (
                <style>{`
                    .recharts-sankey-links path {
                        stroke: #C97B5A !important;
                        stroke-width: 3 !important;
                        stroke-dasharray: 6 10 !important;
                        stroke-opacity: 0.95 !important;
                        animation: money-flow 1.4s linear infinite;
                    }
                    @keyframes money-flow {
                        from { stroke-dashoffset: 0; }
                        to   { stroke-dashoffset: -160; }
                    }
                `}</style>
            )}

            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div className="text-[var(--muted)] text-sm max-w-2xl">
                    Ribbon width scales with dollars. Toggle Working / Retired to preview
                    the shift in retirement. Hit <em>Play</em> to watch it flow.
                </div>
                <div className="flex items-center gap-3">
                    <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-sm"
                         data-testid="mode-toggle">
                        {["working", "retired"].map(k => (
                            <button key={k} onClick={() => setMode(k)}
                                    data-testid={`mode-${k}`}
                                    className={`px-4 py-2 capitalize transition-colors ${
                                        mode === k ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                                   : "hover:bg-black/5"}`}>
                                {k}
                            </button>
                        ))}
                    </div>
                    <button onClick={rollup ? clearRollup : loadRollup}
                            disabled={loadingRollup}
                            data-testid="toggle-rollup"
                            className={`btn-ghost flex items-center gap-2 text-sm ${
                                rollup ? "text-[var(--eucalyptus)]" : ""}`}>
                        {loadingRollup ? <ArrowClockwise size={14} className="animate-spin" />
                                       : <Database size={14} weight={rollup ? "fill" : "regular"} />}
                        {rollup ? "Using transactions" : "Use my transactions"}
                    </button>
                    <button onClick={() => setShowEdit(!showEdit)}
                            data-testid="toggle-inputs"
                            className="btn-ghost flex items-center gap-2 text-sm">
                        <Sliders size={14} /> {showEdit ? "Hide" : "Edit"} inputs
                    </button>
                    <button onClick={() => setPlaying(!playing)}
                            data-testid="play-animation"
                            className={`btn-ghost flex items-center gap-2 text-sm ${
                                playing ? "text-[var(--terracotta)]" : ""}`}>
                        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                        {playing ? "Pause" : "Play"}
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
                <KpiCard label="Total Money In"  value={totalIn}  tone="green" tid="kpi-total-in" />
                <KpiCard label="Total Money Out" value={totalOut}                 tid="kpi-total-out" />
                <KpiCard label={netCash >= 0 ? "Net to Save / Invest" : "Cash Shortfall"}
                         value={netCash} tone={netCash >= 0 ? "green" : "red"} tid="kpi-net" />
            </div>

            {rollup && rollup.shortfall?.is_shortfall && (
                <div className="mb-4 p-4 rounded border border-[var(--terracotta)]/50 bg-[var(--terracotta)]/8 text-sm leading-relaxed"
                     data-testid="shortfall-banner"
                     style={{ background: "rgba(201, 123, 90, 0.08)" }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Warning size={16} weight="fill" className="text-[var(--terracotta)]" />
                        <span className="label-eyebrow text-[var(--terracotta)]">Cash shortfall detected</span>
                    </div>
                    <div className="text-[var(--ink)]">
                        The last 3 months ({rollup.shortfall.labels.join(", ")}) are averaging
                        {" "}<strong className="font-mono-num">{AUD(rollup.shortfall.net_avg)}</strong>/mo net —
                        total <strong className="font-mono-num">{AUD(rollup.shortfall.net_total)}</strong> in the red.
                        Lifestyle spend is running ahead of income. Check the Cash Flow page for the category breakdown.
                    </div>
                </div>
            )}

            {rollup && (
                <div className="mb-4 p-3 rounded border border-[var(--eucalyptus)]/40 bg-[var(--eucalyptus)]/5 text-xs leading-relaxed"
                     data-testid="rollup-banner">
                    <div className="flex items-center gap-2 mb-1">
                        <Database size={12} weight="fill" className="text-[var(--eucalyptus)]" />
                        <span className="label-eyebrow text-[var(--eucalyptus)]">Sourced from transactions</span>
                        <span className="text-[var(--muted)]">
                            {rollup.range.from} → {rollup.range.to} · {rollup.range.months} months
                        </span>
                    </div>
                    <div className="text-[var(--muted)]">
                        Overlaid: {Object.entries(rollup.rollup)
                            .filter(([, v]) => v > 0)
                            .map(([k, v]) => `${k.replace(/_/g, " ")} ${AUD(v)}`)
                            .join(" · ")}
                        {" · "}
                        Non-transaction fields (PAYG, external income, business, SMSF contribs) keep their planning values.
                    </div>
                </div>
            )}

            <div className="hairline bg-white p-4 rounded mb-6 text-xs text-[var(--muted)] leading-relaxed"
                 data-testid="internal-transfers">
                <span className="label-eyebrow mr-3">Internal transfers</span>
                <span>Director&rsquo;s Fees {AUD(inputs.directors_fees)} · {L.business_retained} {AUD(pendazRetained(inputs))}</span>
                {mode === "working" && (inputs.smsf_concessional + inputs.smsf_ncc) > 0 && (
                    <span> · SMSF Contributions {AUD(inputs.smsf_concessional + inputs.smsf_ncc)}
                        {inputs.smsf_ncc > 0 && ` (${AUD(inputs.smsf_concessional)} concessional + ${AUD(inputs.smsf_ncc)} NCC)`}
                    </span>
                )}
                <span className="block mt-1 opacity-70">These are family-internal — they reshape the ribbons but not the top-level In / Out totals.</span>
            </div>

            {showEdit && (
                <div className="hairline bg-white p-6 rounded mb-6" data-testid="inputs-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-eyebrow">Annual inputs — {mode}</div>
                        <button onClick={applyPreset} data-testid="reset-defaults"
                                className="text-xs text-[var(--eucalyptus)] hover:underline">
                            Reset to defaults
                        </button>
                    </div>
                    <div className="grid md:grid-cols-4 gap-3">
                        {(mode === "working" ? [
                            ["salary_gross","Salary (Gross)"],
                            ["salary_tax","PAYG Tax on Salary"],
                            ["dividends_personal","Personal Dividends"],
                            ["rental_income","Rental Income"],
                            ["business_income", L.business_income],
                            ["business_expenses", L.business_expenses_input],
                            ["gbe_dividends", L.ext_dividends_short],
                            ["gbe_loan_repayment", L.ext_loan_rep_short],
                            ["directors_fees","Director\u2019s Fees"],
                            ["smsf_concessional", L.smsf_cc],
                            ["smsf_ncc","SMSF Non-Concessional"],
                            ["lifestyle","Lifestyle Expenses"],
                            ["loan_repayments","Loan Repayments"],
                            ["joe_loan", L.joe_loan_input],
                            ["personal_invest","Personal Investments"],
                            ["smsf_dividends","SMSF Investment Income"],
                            ["smsf_reinvest","SMSF Reinvestment"],
                        ] : [
                            ["dividends_personal","Personal Dividends"],
                            ["rental_income","Rental Income"],
                            ["smsf_pension","SMSF Pension Payments"],
                            ["portfolio_drawdown","Portfolio Drawdown"],
                            ["gbe_dividends", L.ext_dividends_short],
                            ["gbe_loan_repayment", L.ext_loan_rep_short],
                            ["directors_fees","Director\u2019s Fees"],
                            ["lifestyle","Lifestyle Expenses"],
                            ["loan_repayments","Loan Repayments"],
                            ["joe_loan", L.joe_loan_input],
                            ["smsf_dividends","SMSF Investment Income"],
                            ["smsf_reinvest","SMSF Reinvestment"],
                        ]).map(([k, label]) => (
                            <label key={k} className="block">
                                <div className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">{label}</div>
                                <input type="number" value={inputs[k] ?? 0}
                                       onChange={e => setMany({ [k]: Number(e.target.value) || 0 })}
                                       data-testid={`input-${k}`}
                                       className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
                            </label>
                        ))}
                    </div>
                    <div className="text-xs text-[var(--muted)] mt-4 leading-relaxed space-y-2">
                        <div><strong>{L.business_retained} is calculated, not entered</strong> — it&rsquo;s the residual
                        inside the business each year (Business + external income − Business Expenses − Director&rsquo;s Fees).
                        This is annual retained earnings, <em>not</em> the balance sheet total.</div>
                        <div><strong>Salary is split three ways</strong> — take-home (Salary → Personal Cash),
                        PAYG tax (Salary → Income Tax), and salary-sacrifice (Salary → SMSF Contributions).
                        Non-concessional contributions come from Personal Cash after tax.{" "}
                        {isDemoMode()
                            ? "For couples, the bring-forward NCC rule allows up to $360K over 3 years each."
                            : "You\u2019re currently maxed on bring-forward NCC through FY28; from FY29 you can each do $120K/yr ($240K combined)."}
                        </div>
                        <div><strong>Director&rsquo;s Fees are internal transfers</strong> — they move money from
                        {" "}{L.business_cash} → Personal Cash, so they don&rsquo;t change Total Money In / Out.
                        Increasing them shrinks the {L.business_retained} ribbon and grows Personal Cash instead.</div>
                    </div>
                </div>
            )}

            <div className="hairline bg-white p-4 md:p-6 rounded" style={{ minHeight: 560 }}
                 data-testid="sankey-container">
                <ResponsiveContainer width="100%" height={560}>
                    <Sankey
                        data={{ nodes, links }}
                        nodePadding={26} nodeWidth={12}
                        margin={{ left: 140, right: 200, top: 20, bottom: 20 }}
                        link={{ stroke: "#5F8071", strokeOpacity: 0.35 }}
                        node={renderNode}
                    >
                        <Tooltip formatter={(v) => AUD(v)}
                                 contentStyle={{ background: "white", border: "1px solid var(--line)",
                                                 fontSize: 12, borderRadius: 4 }} />
                    </Sankey>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 text-xs text-[var(--muted)] leading-relaxed">
                <strong>How to read this:</strong> money enters from the left (income sources), flows through
                cash accounts (&ldquo;buckets&rdquo;), and exits on the right (expenses, loan repayments, investments).
                Ribbon thickness = dollar value. Hover any ribbon to see the exact amount.
                {L.footer_note}
            </div>
        </div>
    );
}

function KpiCard({ label, value, sub, tone, tid }) {
    const bg = tone === "green" ? "var(--eucalyptus)" : tone === "red" ? "var(--terracotta)" : "white";
    const fg = tone ? "var(--bone)" : "var(--ink)";
    const border = tone ? "transparent" : "var(--line)";
    return (
        <div data-testid={tid} className="p-6 rounded"
             style={{ background: bg, color: fg, border: `1px solid ${border}` }}>
            <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
            <div className="font-serif text-3xl mt-2 font-mono-num">{AUD(value)}</div>
            {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
        </div>
    );
}
